import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  ShieldCheck,
  Award,
  Calendar,
  User,
  BookOpen,
  Printer,
  CheckCircle2,
  AlertCircle,
  Search,
  ExternalLink,
} from 'lucide-react';
import { supabase } from '../../lib/supabase';
import type { Certificate } from '../../types/database';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';

export default function CertificateVerificationPage() {
  const { code } = useParams<{ code: string }>();
  const [searchCode, setSearchCode] = useState(code || '');
  const [certificate, setCertificate] = useState<Certificate | null>(null);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);

  useEffect(() => {
    if (code) {
      setSearchCode(code);
      verifyCertificate(code);
    }
  }, [code]);

  const verifyCertificate = async (certCode: string) => {
    if (!certCode.trim()) return;
    setLoading(true);
    setSearched(true);

    try {
      const { data, error } = await supabase
        .from('certificates')
        .select('*')
        .eq('certificate_code', certCode.trim().toUpperCase())
        .maybeSingle();

      if (data) {
        setCertificate(data as Certificate);
      } else {
        setCertificate(null);
      }
    } catch (err) {
      console.error('Verification error:', err);
      setCertificate(null);
    } finally {
      setLoading(false);
    }
  };

  const handleSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    verifyCertificate(searchCode);
  };

  const handlePrint = () => {
    window.print();
  };

  return (
    <div className="min-h-screen flex flex-col bg-gray-50">
      <Navbar />

      <main className="flex-1 pt-32 pb-20 px-4">
        <div className="max-w-4xl mx-auto">
          {/* Header & Verification Search */}
          <div className="text-center mb-10">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-50 text-emerald-700 border border-emerald-200 rounded-full text-xs font-semibold uppercase tracking-wider mb-4">
              <ShieldCheck className="w-4 h-4" />
              <span>Official Credential Registry</span>
            </div>
            <h1 className="text-3xl sm:text-4xl font-extrabold text-[#1A2E4E] mb-3">
              Certificate Verification Portal
            </h1>
            <p className="text-sm text-gray-500 max-w-lg mx-auto">
              Authenticate credentials issued by BientoMart Online Academy for employees, candidates, and students.
            </p>

            {/* Search Input */}
            <form onSubmit={handleSearchSubmit} className="mt-6 max-w-md mx-auto flex gap-2">
              <div className="relative flex-1">
                <Search className="w-5 h-5 absolute left-3.5 top-3 text-gray-400" />
                <input
                  type="text"
                  value={searchCode}
                  onChange={(e) => setSearchCode(e.target.value)}
                  placeholder="Enter Certificate Code (e.g. BM-2026-X8K9M2)"
                  className="w-full pl-11 pr-4 py-2.5 bg-white border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-clairBlue-500 text-xs sm:text-sm uppercase font-mono"
                />
              </div>
              <button
                type="submit"
                disabled={loading}
                className="px-5 py-2.5 bg-darkBlue-600 text-white rounded-xl font-bold text-xs hover:bg-darkBlue-700 transition-colors flex items-center gap-2 disabled:opacity-50"
              >
                {loading ? 'Checking...' : 'Verify'}
              </button>
            </form>
          </div>

          {/* Verification Results */}
          {loading ? (
            <div className="text-center py-16">
              <div className="w-10 h-10 border-4 border-darkBlue-600 border-t-transparent rounded-full animate-spin mx-auto mb-3" />
              <p className="text-xs text-gray-500 font-medium">Verifying against PostgreSQL registry...</p>
            </div>
          ) : certificate ? (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-white rounded-3xl shadow-xl border border-gray-100 p-8 sm:p-12 relative overflow-hidden"
            >
              {/* Decorative Border */}
              <div className="absolute top-0 left-0 right-0 h-3 bg-gradient-to-r from-darkBlue-600 via-clairBlue-500 to-amber-500" />

              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 border-b border-gray-100 pb-6 mb-8">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-2xl bg-emerald-50 border border-emerald-200 flex items-center justify-center text-emerald-600">
                    <CheckCircle2 className="w-7 h-7" />
                  </div>
                  <div>
                    <span className="text-xs font-bold uppercase tracking-wider text-emerald-600 block">
                      Status: Verified Authentic
                    </span>
                    <span className="text-xs text-gray-400 font-mono">
                      Registry ID: {certificate.certificate_code}
                    </span>
                  </div>
                </div>

                <button
                  onClick={handlePrint}
                  className="px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-xl text-xs font-bold flex items-center gap-2 transition-colors"
                >
                  <Printer className="w-4 h-4" />
                  <span>Print Certificate</span>
                </button>
              </div>

              {/* Certificate Template Display */}
              <div className="text-center py-8 border-4 border-double border-amber-200/80 rounded-3xl bg-gradient-to-b from-amber-50/20 to-white p-6 sm:p-10 mb-8">
                <div className="w-16 h-16 mx-auto rounded-full bg-gradient-to-tr from-amber-400 to-amber-600 text-white flex items-center justify-center mb-4 shadow-md">
                  <Award className="w-8 h-8" />
                </div>

                <p className="text-xs uppercase tracking-widest text-gray-500 font-semibold mb-2">
                  BientoMart Online Academy
                </p>
                <h2 className="text-xl sm:text-2xl font-black text-[#1A2E4E] uppercase tracking-wide mb-6">
                  Certificate of Achievement
                </h2>

                <p className="text-xs text-gray-500 italic mb-2">This is proudly presented to</p>
                <h3 className="text-2xl sm:text-3xl font-extrabold text-darkBlue-900 border-b border-gray-200 inline-block px-8 pb-2 mb-6">
                  {certificate.recipient_name}
                </h3>

                <p className="text-xs text-gray-500 max-w-lg mx-auto mb-3">
                  for successfully completing all curriculum modules, practical assessments, and examinations for
                </p>

                <h4 className="text-lg sm:text-xl font-bold text-gray-800 mb-6">
                  {certificate.course_title}
                </h4>

                <div className="grid grid-cols-2 max-w-md mx-auto border-t border-gray-200 pt-6 text-xs text-gray-600">
                  <div>
                    <span className="block font-bold text-gray-800">{certificate.instructor_name}</span>
                    <span className="text-[11px] text-gray-400">Lead Instructor</span>
                  </div>
                  <div>
                    <span className="block font-bold text-gray-800">
                      {new Date(certificate.issue_date).toLocaleDateString('en-US', {
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric',
                      })}
                    </span>
                    <span className="text-[11px] text-gray-400">Date Issued</span>
                  </div>
                </div>
              </div>

              {/* Detailed Breakdown */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 text-xs">
                <div className="p-4 bg-gray-50 rounded-2xl">
                  <span className="text-gray-400 block mb-1">Assessment Grade</span>
                  <span className="font-bold text-gray-800 text-sm">{certificate.grade || 'Distinction'}</span>
                </div>
                <div className="p-4 bg-gray-50 rounded-2xl">
                  <span className="text-gray-400 block mb-1">Issuing Institution</span>
                  <span className="font-bold text-gray-800 text-sm">BientoMart Academy (Uganda)</span>
                </div>
                <div className="p-4 bg-gray-50 rounded-2xl">
                  <span className="text-gray-400 block mb-1">Record Integrity</span>
                  <span className="font-bold text-emerald-600 text-sm flex items-center gap-1">
                    <CheckCircle2 className="w-4 h-4" />
                    Verified in PostgreSQL
                  </span>
                </div>
              </div>
            </motion.div>
          ) : searched ? (
            <div className="text-center py-16 bg-white rounded-3xl border border-gray-100 p-8">
              <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-3" />
              <h3 className="text-lg font-bold text-gray-800 mb-1">No Matching Certificate</h3>
              <p className="text-xs text-gray-500 max-w-md mx-auto mb-6">
                No active certificate was found in our database matching code "{searchCode}". Please check the code for typos and try again.
              </p>
              <Link to="/academy" className="text-xs font-semibold text-clairBlue-600 hover:underline">
                Explore Academy Courses →
              </Link>
            </div>
          ) : null}
        </div>
      </main>

      <Footer />
    </div>
  );
}
